import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ArrowDownRight,
  ArrowUpRight,
  Sparkles,
  TrendingUp,
  Banknote,
  Receipt,
  PiggyBank,
  AlertTriangle,
  Package,
  Clock,
} from "lucide-react";
import useCustomerData from "../hooks/useCustomerData";
import MetricDisplay from "../components/MetricDisplay";
import KpiValue from "../components/KpiValue";
import ConnectTallyBanner from "../components/ConnectTallyBanner";
import { InsightsGrid } from "../components/intel/IntelKit";
import { api } from "../lib/api";

const TABS = [
  { id: "overview",  label: "Overview" },
  { id: "pnl",       label: "P&L" },
  { id: "cashflow",  label: "Cash Flow" },
  { id: "ccc",       label: "CCC" },
  { id: "inventory", label: "Inventory" },
  { id: "margins",   label: "Margins" },
];
const TAB_HASHES = TABS.map((t) => `#${t.id}`);

const STATUS_STYLES = {
  green: { card: "bg-[#DCFCE7] border-emerald-100", text: "text-emerald-700", dot: "bg-emerald-500", bar: "bg-emerald-500" },
  amber: { card: "bg-[#FEF3C7] border-amber-100",   text: "text-amber-700",   dot: "bg-amber-500",   bar: "bg-amber-500"   },
  red:   { card: "bg-[#FEE2E2] border-red-100",     text: "text-red-700",     dot: "bg-red-500",     bar: "bg-red-500"     },
  blue:  { card: "bg-[#DBEAFE] border-blue-100",    text: "text-blue-700",    dot: "bg-blue-500",    bar: "bg-blue-500"    },
};

// ---------------------------------------------------------------------------
// Reusable bits
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Format helpers + dynamic dates (used across every tab for active customers)
// ---------------------------------------------------------------------------
const DASH = "—";

function monthYearLabel(d = new Date()) {
  return `${d.toLocaleString("en-IN", { month: "long" })} ${d.getFullYear()}`;
}
function monthYearShort(d = new Date()) {
  return d.toLocaleString("en-IN", { month: "short", year: "2-digit" });
}
function fmtINR(n) {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return null;
  const num = Number(n);
  const v = Math.abs(num);
  const sign = num < 0 ? "-" : "";
  if (v >= 1_00_00_000) return `${sign}₹${(v / 1_00_00_000).toFixed(2)}Cr`;
  if (v >= 1_00_000)    return `${sign}₹${(v / 1_00_000).toFixed(1)}L`;
  if (v >= 1000)        return `${sign}₹${(v / 1000).toFixed(1)}K`;
  return `${sign}₹${Math.round(v).toLocaleString("en-IN")}`;
}
function fmtPct(n)   { return (n === null || n === undefined || Number.isNaN(Number(n))) ? null : `${Number(n).toFixed(1)}%`; }
function fmtDays(n)  { return (n === null || n === undefined || Number.isNaN(Number(n))) ? null : `${Number(n).toFixed(1)} days`; }
function fmtTurns(n) { return (n === null || n === undefined || Number.isNaN(Number(n))) ? null : `${Number(n).toFixed(1)}x`; }
function relativeTimeFrom(iso) {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  const diff = Math.max(0, Date.now() - t);
  const mins = Math.floor(diff / 60000);
  if (mins < 1)    return "just now";
  if (mins < 60)   return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)    return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

/**
 * Real-data MiniKPI. Shows the formatted real value, or a dash + tooltip when
 * the underlying metric is null. Visitors should NOT use this — they keep the
 * existing hardcoded <MiniKPI /> markup.
 */
function RealMiniKPI({ icon: Icon, label, value, format = "currency", status = "blue", sub, tooltip, testId }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.blue;
  return (
    <div data-testid={testId} className={`rounded-xl border ${s.card} p-4`}>
      <div className="flex items-center justify-between text-[11px] font-semibold uppercase tracking-wider text-slate-500">
        <span className="inline-flex items-center gap-1.5">
          {Icon && <Icon size={13} className={s.text} />}
          {label}
        </span>
        <span className={`h-1.5 w-1.5 rounded-full ${s.dot}`} />
      </div>
      <div className="mt-2">
        <MetricDisplay
          value={value}
          format={format}
          label={label}
          tooltipText={tooltip}
          size="large"
        />
      </div>
      {sub && <div className="mt-1 text-[11.5px] text-slate-500">{sub}</div>}
    </div>
  );
}

function SectionCard({ title, subtitle, right, children, testid }) {  return (
    <div data-testid={testid} className="rounded-xl border border-slate-200 bg-white p-5 shadow-card">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{title}</div>
          {subtitle && <div className="text-[13px] text-slate-500">{subtitle}</div>}
        </div>
        {right}
      </div>
      <div className="mt-4 core-scroll-x">{children}</div>
    </div>
  );
}

function StatusPill({ status, children }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.blue;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10.5px] font-medium ${s.card} ${s.text}`}>
      {children}
    </span>
  );
}

/**
 * GST Compliance Status — own-GSTIN registration + filing status, reported
 * straight from GSTN (via the existing Supplier Audit GST service). STATUS
 * ONLY — no amounts, no scoring. We report the source's status; we do not
 * certify or attest to compliance.
 */
function GSTComplianceStatusCard({ customerId }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!customerId) { setLoading(false); setStatus(null); return; }
    let alive = true;
    setLoading(true);
    api.gstComplianceStatus(customerId).then((res) => {
      if (!alive) return;
      setStatus(res.ok ? res.data : { available: false, message: "Status unavailable — could not reach GSTN" });
      setLoading(false);
    });
    return () => { alive = false; };
  }, [customerId]);

  if (!customerId) return null;

  const regDot = { active: "green", suspended: "amber", cancelled: "red" }[status?.registration_status] || "blue";
  const regLabel = { active: "Active", suspended: "Suspended", cancelled: "Cancelled" }[status?.registration_status] || "Unknown";

  let filingLine = null;
  if (status?.available) {
    if (status.filing_check_status === "returned_data") {
      if (status.filing_status === "filing_regularly") {
        filingLine = `Filed through ${status.last_return_period || "recently"}, per GST portal`;
      } else if (status.filing_status === "filing_delayed") {
        filingLine = `Filing behind — last filed ${status.last_return_period || "a while back"}, per GST portal`;
      } else {
        filingLine = "Not filing, per GST portal";
      }
    } else if (status.filing_check_status === "not_applicable") {
      filingLine = status.reason === "recently_registered_no_filing_due_yet"
        ? "Too newly registered for a filing to be due yet, per GST portal"
        : "No filing due — registration cancelled, per GST portal";
    } else {
      filingLine = "Filing status unavailable";
    }
  }

  return (
    <SectionCard testid="cfo-gst-compliance" title="GST Compliance Status" subtitle="Per GSTN — reported, not certified by C-ORE">
      {loading ? (
        <div data-testid="cfo-gst-compliance-loading" className="text-[13px] text-slate-400">Checking GSTN…</div>
      ) : !status?.available ? (
        <div data-testid="cfo-gst-compliance-unavailable" className="flex items-center gap-2 text-[13px] text-slate-500">
          <AlertTriangle size={14} className="text-slate-400" />
          {status?.message || "GST status unavailable"}
        </div>
      ) : (
        <div data-testid="cfo-gst-compliance-available" className="flex flex-wrap items-center gap-x-6 gap-y-2">
          <div className="flex items-center gap-2">
            <span className={`h-2 w-2 rounded-full ${STATUS_STYLES[regDot].dot}`} />
            <span className="text-[13px] font-semibold text-slate-700">Per GSTN: {regLabel}</span>
          </div>
          <div className="text-[13px] text-slate-600">{filingLine}</div>
          <div className="text-[11.5px] text-slate-400">Checked {relativeTimeFrom(status.checked_at) || "just now"}</div>
        </div>
      )}
    </SectionCard>
  );
}

// ---------------------------------------------------------------------------
// 1) OVERVIEW
// ---------------------------------------------------------------------------
function ARAgingBreakdown({ debtors = null, isLoading = false }) {
  // Real data path: bucket the debtors array by days_outstanding
  if (debtors && Array.isArray(debtors)) {
    const buckets = [
      { label: "0–30 days",  status: "green", amount: 0 },
      { label: "31–60 days", status: "amber", amount: 0 },
      { label: "60+ days",   status: "red",   amount: 0 },
    ];
    let total = 0;
    debtors.forEach((d) => {
      const days = Number(d.days_outstanding || 0);
      const amt = Number(d.outstanding_amount || 0);
      total += amt;
      if (days <= 30) buckets[0].amount += amt;
      else if (days <= 60) buckets[1].amount += amt;
      else buckets[2].amount += amt;
    });
    const fmtINR = (n) => {
      if (n >= 1_00_00_000) return `₹${(n / 1_00_00_000).toFixed(2)}Cr`;
      if (n >= 1_00_000)    return `₹${(n / 1_00_000).toFixed(1)}L`;
      if (n >= 1000)        return `₹${(n / 1000).toFixed(0)}K`;
      return `₹${Math.round(n)}`;
    };
    const subtitle = total > 0 ? `${fmtINR(total)} total receivables` : "No outstanding debtors";
    return (
      <SectionCard testid="cfo-ar-aging" title="Debtors Aging" subtitle={subtitle}>
        {total === 0 ? (
          <div className="rounded-md border border-dashed border-slate-200 bg-slate-50 px-3 py-4 text-center text-[12px] text-slate-500">
            Debtor data will appear here after your next Tally sync with open invoices.
          </div>
        ) : (
          <ul className="space-y-3">
            {buckets.map((b) => {
              const pct = total > 0 ? Math.round((b.amount / total) * 100) : 0;
              return (
                <li key={b.label}>
                  <div className="flex items-center justify-between text-[12.5px]">
                    <span className="font-medium text-[#0F1B2D]">{b.label}</span>
                    <span className="font-semibold text-[#0F1B2D]">{fmtINR(b.amount)}</span>
                  </div>
                  <div className="mt-1 h-1.5 w-full rounded-full bg-slate-100">
                    <div className={`h-1.5 rounded-full ${STATUS_STYLES[b.status].bar}`} style={{ width: `${pct}%`, transition: "width 600ms ease" }} />
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </SectionCard>
    );
  }

  // Active customer + no debtors yet — explicit empty state, no demo numbers.
  return (
    <SectionCard testid="cfo-ar-aging" title="Debtors Aging" subtitle="Awaiting first Tally sync">
      <div className="rounded-md border border-dashed border-blue-200 bg-blue-50/50 px-3 py-6 text-center text-[12px] text-blue-700">
        Connect Tally to see your live debtor ageing buckets here.
      </div>
    </SectionCard>
  );
}

function WorkingCapitalTrend() {
  return (
    <SectionCard
      testid="cfo-wc-trend"
      title="Working Capital · 8 months"
      subtitle="Awaiting first Tally sync"
    >
      <div className="flex h-[170px] items-center justify-center rounded-md border border-dashed border-blue-200 bg-blue-50/50 text-center text-[12px] text-blue-700">
        Connect Tally to see your working-capital trend across the last 8 months.
      </div>
    </SectionCard>
  );
}

function CFOBrief({ dataAvailable }) {
  // Visitor / demo path — keep the existing hardcoded copy unchanged.
  

  // Active customer: fetch a real CFO brief from /api/ai-insights/generate.
  const [text, setText] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ok | empty | error

  useEffect(() => {
    let cancelled = false;
    if (!dataAvailable) { setStatus("empty"); return () => { cancelled = true; }; }
    setStatus("loading");
    api.aiInsightsGenerate("cfo", null).then((res) => {
      if (cancelled) return;
      if (!res.ok) { setStatus("error"); return; }
      const items = res.data?.items || [];
      if (items.length === 0) { setStatus("empty"); return; }
      // Use the highest-priority (first) insight body as the brief.
      const top = items[0];
      const parts = [top.body, top.action].filter(Boolean).join(" ");
      setText(parts || top.title);
      setStatus("ok");
    });
    return () => { cancelled = true; };
  }, [dataAvailable]);

  if (status === "loading") {
    return (
      <div data-testid="cfo-brief" className="rounded-xl border border-blue-100 bg-[#EFF6FF] p-5 shadow-card">
        <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-blue-700">
          <Sparkles size={13} /> CFO Brief
        </div>
        <p className="mt-2 text-[13px] leading-snug text-slate-600 core-pulse">
          Generating your CFO brief…
        </p>
      </div>
    );
  }

  if (status === "ok" && text) {
    return (
      <div data-testid="cfo-brief" className="rounded-xl border border-emerald-100 bg-[#F0FDF4] p-5 shadow-card">
        <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-emerald-700">
          <Sparkles size={13} /> CFO Brief
          <span className="ml-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10.5px] font-medium text-emerald-700">C-ORE generated</span>
        </div>
        <p className="mt-2 text-[13px] leading-snug text-[#0F1B2D]">{text}</p>
      </div>
    );
  }

  // empty / error — informative copy, no demo numbers.
  return (
    <div data-testid="cfo-brief" className="rounded-xl border border-blue-100 bg-[#EFF6FF] p-5 shadow-card">
      <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-blue-700">
        <Sparkles size={13} /> CFO Brief
      </div>
      <p className="mt-2 text-[13px] leading-snug text-slate-700">
        Your CFO brief generates after Tally syncs. Connect Tally and run a sync to unlock a personalised summary
        of your cash position, runway, top overdue debtors and the single move with the most cash impact this week.
      </p>
    </div>
  );
}

function CFOOverview() {
  const { customerData, dataAvailable, isLoading, customer } = useCustomerData();
  const fin = customerData?.financial || {};
  const der = customerData?.derived  || {};
  const gst = customerData?.gst      || {};

  // - Visitors / demo session  → keep hardcoded demo copy.
  // - Active customer + data   → real value (formatted).
  // - Active customer + no data → dash + info tooltip.
  const card = (label, icon, status, demoText, realValue, sub, format, tip) => (
    <div className={`rounded-xl border ${STATUS_STYLES[status].card} p-4`}>
      <div className="flex items-center justify-between text-[11px] font-semibold uppercase tracking-wider text-slate-500">
        <span className="inline-flex items-center gap-1.5">
          {icon}
          {label}
        </span>
        <span className={`h-1.5 w-1.5 rounded-full ${STATUS_STYLES[status].dot}`} />
      </div>
      <div className="mt-2">
        <KpiValue
          demoValue={demoText}
          realValue={realValue}
          format={format}
          label={label}
          tooltip={tip}
          size="large"
          isLoading={isLoading}
          dataAvailable={dataAvailable}
        />
      </div>
      {sub && <div className="mt-1 text-[11.5px] text-slate-500">{sub}</div>}
    </div>
  );

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3 desktop:grid-cols-4">
        {card("Cash Now", <Banknote size={13} className="text-emerald-700" />, "green",
              "₹14.2L", dataAvailable ? fin.cash : null,
              dataAvailable ? "across all banks" : ("Connect Tally"),
              "currency",
              "Your total cash and bank balance right now. Updated every hour when Tally is open.")}
        {card("GST Net Payable", <Receipt size={13} className="text-amber-700" />, "amber",
              "₹2.57L", dataAvailable ? gst.gst_payable : null,
              dataAvailable ? "this period" : ("Connect Tally"),
              "currency",
              "Net GST payable to government this month after deducting your input tax credit.")}
        {card("Total Outstanding", <PiggyBank size={13} className="text-blue-700" />, "blue",
              "₹23.6L", dataAvailable ? der.total_debtors : null,
              dataAvailable ? `${(customerData?.debtors || []).length} customers` : ("Connect Tally"),
              "currency",
              "Total money owed to you by customers not yet paid. From Tally sundry debtors ledgers.")}
        {card("Gross Margin", <TrendingUp size={13} className="text-green-700" />, "green",
              "18.3%", dataAvailable ? fin.gross_margin_pct : null,
              dataAvailable ? "this period" : ("Connect Tally"),
              "percentage",
              "Revenue minus direct costs divided by revenue. Target above 20%.")}
      </div>

      <GSTComplianceStatusCard customerId={customer?.id} />

      <div className="grid grid-cols-1 gap-4 tablet:grid-cols-2 desktop:grid-cols-5">
        <div className="desktop:col-span-3"><WorkingCapitalTrend /></div>
        <div className="desktop:col-span-2">
          <ARAgingBreakdown
            debtors={dataAvailable ? customerData.debtors : null}
            isLoading={isLoading}
          />
        </div>
      </div>
      <CurrentRatioCard isLoading={isLoading} />
      <CFOBrief dataAvailable={dataAvailable} />
      {
        <InsightsGrid
          subtitle="Where your finances stand right now · refreshed from your latest Tally sync"
          fallback={[]}
          fetchFn={(force) => api.aiInsightsGenerate("cfo", null, force)}
          testidPrefix="cfo"
          dataAvailable={dataAvailable}
        />
      }
    </div>
  );
}

function CurrentRatioCard({ isLoading }) {
  return (
    <SectionCard
      testid="cfo-current-ratio"
      title="Current Ratio"
      subtitle="Current assets ÷ current liabilities"
    >
      <div className="flex items-center gap-3">
        <MetricDisplay
          value={null}
          format="number"
          label="Current Ratio"
          tooltipText="This requires balance sheet data. Available once full Tally integration with balance sheet sync is complete."
          size="large"
          isLoading={isLoading}
        />
        <span className="text-[12px] text-slate-500">
          Balance sheet sync coming soon
        </span>
      </div>
    </SectionCard>
  );
}

// ---------------------------------------------------------------------------
// 2) P&L
// ---------------------------------------------------------------------------
function CFOPnl() {
  const { customerData, dataAvailable, isLoading } = useCustomerData();

  // Visitors / demo session — keep the original hardcoded P&L unchanged.
  

  // Active customer — real values or dash.
  const raw  = (dataAvailable && customerData?.raw)           || {};
  const prof = (dataAvailable && customerData?.profitability) || {};

  const revenue  = raw.revenue_mtd ?? null;
  const rawMat   = raw.raw_material ?? null;
  const labour   = raw.direct_labour ?? null;
  const overhead = raw.overhead ?? null;
  const gp = (revenue !== null && (rawMat !== null || labour !== null))
    ? Number(revenue) - Number(rawMat || 0) - Number(labour || 0)
    : null;
  const ebitda    = prof.ebitda ?? null;
  const netProfit = raw.net_profit ?? null;

  const moneyCell = (v) => {
    const s = fmtINR(v);
    return s ? (
      <td className="py-2 text-right tabular-nums text-[#0F1B2D]">{s}</td>
    ) : (
      <td className="py-2 text-right tabular-nums text-slate-400">{DASH}</td>
    );
  };

  const rows = [
    { id: "rev_sales",   label: "Revenue",        value: revenue,   kind: "data" },
    { id: "cogs_mat",    label: "Raw Material",   value: rawMat !== null ? -Math.abs(rawMat) : null,   kind: "data" },
    { id: "cogs_labour", label: "Direct Labour",  value: labour !== null ? -Math.abs(labour) : null,   kind: "data" },
    { id: "gross_profit",label: "Gross Profit",   value: gp,        kind: "subtotal", highlight: true,
      margin: prof.gross_margin_pct ?? null },
    { id: "opex_oh",     label: "Overhead",       value: overhead !== null ? -Math.abs(overhead) : null, kind: "data" },
    { id: "ebitda",      label: "EBITDA",         value: ebitda,    kind: "subtotal", highlight: true,
      margin: prof.ebitda_margin ?? null },
    { id: "net_profit",  label: "Net Profit",     value: netProfit, kind: "subtotal", highlight: true,
      margin: prof.net_profit_margin ?? null },
  ];

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3 desktop:grid-cols-4">
        <RealMiniKPI
          testId="pnl-kpi-revenue"
          icon={TrendingUp} label="Revenue MTD"
          value={revenue} format="currency" status="green"
          sub={revenue !== null ? "this month" : null}
          tooltip="Total revenue this month from your Tally sales ledgers."
        />
        <RealMiniKPI
          testId="pnl-kpi-gross-profit"
          icon={TrendingUp} label="Gross Profit"
          value={gp} format="currency" status="green"
          sub={prof.gross_margin_pct != null ? `margin ${fmtPct(prof.gross_margin_pct)}` : null}
          tooltip="Revenue minus raw material and direct labour. Needs both COGS components synced from Tally."
        />
        <RealMiniKPI
          testId="pnl-kpi-ebitda"
          icon={TrendingUp} label="EBITDA"
          value={ebitda} format="currency" status="green"
          sub={prof.ebitda_margin != null ? `margin ${fmtPct(prof.ebitda_margin)}` : null}
          tooltip="Earnings before interest, tax, depreciation and amortisation. Computed from your revenue and operating costs."
        />
        <RealMiniKPI
          testId="pnl-kpi-net-profit"
          icon={TrendingUp} label="Net Profit"
          value={netProfit} format="currency" status="green"
          sub={prof.net_profit_margin != null ? `margin ${fmtPct(prof.net_profit_margin)}` : null}
          tooltip="Final profit after all expenses, interest and tax — pulled from your Tally P&L."
        />
      </div>

      <SectionCard
        testid="cfo-pnl-table"
        title={`Profit & Loss · ${monthYearLabel()}`}
        subtitle="From your latest Tally snapshot. Missing rows appear after their data syncs."
      >
        <div className="overflow-x-auto">
          <table className="w-full text-[12.5px]">
            <thead>
              <tr className="text-left text-[10.5px] uppercase tracking-wider text-slate-500">
                <th className="py-2">Line item</th>
                <th className="py-2 text-right">This month</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const isSub = r.kind === "subtotal";
                const rowCls = [
                  isSub ? "border-t border-slate-200 font-semibold" : "border-t border-slate-50",
                  r.highlight ? "bg-emerald-50/40" : "",
                ].join(" ");
                return (
                  <tr key={r.id} data-testid={`pnl-row-${r.id}`} className={rowCls}>
                    <td className="py-2 text-[#0F1B2D]">
                      {r.label}
                      {typeof r.margin === "number" && (
                        <span className="ml-2 text-[10.5px] font-normal text-emerald-600">{fmtPct(r.margin)} margin</span>
                      )}
                    </td>
                    {moneyCell(r.value)}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </SectionCard>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 3) CASH FLOW
// ---------------------------------------------------------------------------
function CashProjection({ cashForecast }) {
  // Real path: 13-week cash forecast from the calculation engine.
  if (Array.isArray(cashForecast) && cashForecast.length > 0) {
    const values = cashForecast.map((d) => d.projected_cash);
    const max = Math.max(...values, 1);
    const min = Math.min(...values, 0);
    const range = Math.max(1, max - min);
    const fmt = (n) => {
      const v = Math.abs(n);
      if (v >= 1_00_00_000) return `₹${(v / 1_00_00_000).toFixed(2)}Cr`;
      if (v >= 1_00_000)    return `₹${(v / 1_00_000).toFixed(1)}L`;
      return `₹${Math.round(v)}`;
    };
    return (
      <SectionCard
        testid="cfo-cash-projection"
        title="13-week cash projection"
        subtitle="Computed from your revenue and expense run-rate"
        right={<span className="text-[11px] text-amber-700">Min {fmt(min)}</span>}
      >
        <div className="flex h-[160px] items-end gap-1">
          {cashForecast.map((d) => {
            const h = 20 + ((d.projected_cash - min) / range) * 80;
            const cls = d.flag === "danger" ? "bg-red-500" : d.flag === "warning" ? "bg-amber-500" : "bg-[#2563EB]";
            return (
              <div
                key={d.week}
                data-testid={`cash-projection-week-${d.week}`}
                className={`flex-1 rounded-t-sm transition-all ${cls} hover:opacity-90`}
                style={{ height: `${h}%` }}
                title={`Week ${d.week}: ${fmt(d.projected_cash)} (${d.flag})`}
              />
            );
          })}
        </div>
        <div className="mt-2 flex justify-between text-[10px] text-slate-500">
          <span>W1</span><span>W7</span><span>W13</span>
        </div>
      </SectionCard>
    );
  }

  // Empty state — applies to both visitors and active customers without a
  // forecast yet. No hardcoded demo bars.
  return (
    <SectionCard
      testid="cfo-cash-projection"
      title="13-week cash projection"
      subtitle="Awaiting first Tally sync"
    >
      <div className="flex h-[160px] items-center justify-center rounded-md border border-dashed border-blue-200 bg-blue-50/50 text-center text-[12px] text-blue-700">
        Connect Tally to see your forward cash trajectory.
      </div>
    </SectionCard>
  );
}

function CFOCashflow() {
  const { customerData, dataAvailable, isLoading } = useCustomerData();

  // Visitors — original hardcoded cash-flow tab unchanged.
  

  // Active customer
  const raw   = (dataAvailable && customerData?.raw)         || {};
  const fcst  = (dataAvailable && customerData?.forecasting) || {};
  const opening = raw.cash ?? null;
  const revenue = raw.revenue_mtd ?? null;
  const expenses = raw.expenses_mtd ?? null;
  const operatingCF = (revenue !== null && expenses !== null)
    ? Number(revenue) - Number(expenses)
    : null;
  const closing = (opening !== null && operatingCF !== null)
    ? Number(opening) + Number(operatingCF)
    : null;

  const cashForecast = fcst.cash_forecast || null;
  const cfMonthLabel = monthYearLabel();

  const rows = [
    { id: "open",   label: `Opening Cash (start of ${cfMonthLabel})`, value: opening },
    { id: "rev",    label: "Revenue collected this month",            value: revenue },
    { id: "exp",    label: "Expenses paid this month",                value: expenses !== null ? -Math.abs(expenses) : null },
    { id: "op_cf",  label: "Operating Cash Flow",                     value: operatingCF, kind: "subtotal", highlight: true },
    { id: "inv_cf", label: "Investing Cash Flow",                     value: null, tooltip: "Requires fixed-asset purchase/sale data from your bank statement feed." },
    { id: "fin_cf", label: "Financing Cash Flow",                     value: null, tooltip: "Requires bank statement connection (loan drawdowns, EMIs, capital infusions)." },
    { id: "close",  label: `Closing Cash (end of ${cfMonthLabel})`,   value: closing, kind: "subtotal", highlight: true },
  ];

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3 desktop:grid-cols-4">
        <RealMiniKPI
          testId="cf-kpi-opening"
          icon={Banknote} label="Opening Cash"
          value={opening} format="currency" status="blue"
          sub={opening !== null ? `start of ${cfMonthLabel}` : null}
          tooltip="Cash + bank balance at the start of this month, from your Tally snapshot."
        />
        <RealMiniKPI
          testId="cf-kpi-op-cf"
          icon={ArrowDownRight} label="Operating CF"
          value={operatingCF} format="currency"
          status={operatingCF !== null && operatingCF < 0 ? "red" : "green"}
          sub={operatingCF !== null ? "revenue − expenses (MTD)" : null}
          tooltip="Monthly revenue collected minus monthly expenses paid."
        />
        <RealMiniKPI
          testId="cf-kpi-fin-cf"
          icon={PiggyBank} label="Financing CF"
          value={null} format="currency" status="amber"
          tooltip="Requires bank statement connection (loan drawdowns, EMIs, capital infusions)."
        />
        <RealMiniKPI
          testId="cf-kpi-closing"
          icon={Banknote} label="Closing Cash"
          value={closing} format="currency"
          status={closing !== null && closing < (opening || 0) ? "amber" : "green"}
          sub={closing !== null ? "opening + operating CF" : null}
          tooltip="Projected end-of-month cash = opening cash plus operating cash flow this month."
        />
      </div>

      <CashProjection cashForecast={cashForecast} />

      <SectionCard
        testid="cfo-cashflow-table"
        title={`Cash Flow Statement · ${cfMonthLabel}`}
        subtitle="Sources & uses of cash · derived from your Tally snapshot"
      >
        <table className="w-full text-[12.5px]">
          <tbody>
            {rows.map((r) => {
              const isSub = r.kind === "subtotal";
              const negative = typeof r.value === "number" && r.value < 0;
              const rowCls = [
                isSub ? "border-t border-slate-200 font-semibold" : "border-t border-slate-50",
                r.highlight ? "bg-emerald-50/40" : "",
              ].join(" ");
              const formatted = fmtINR(r.value);
              return (
                <tr key={r.id} data-testid={`cf-row-${r.id}`} className={rowCls}>
                  <td className="py-2 text-[#0F1B2D]">{r.label}</td>
                  <td className={`py-2 text-right tabular-nums ${negative ? "text-red-600" : (formatted ? "text-[#0F1B2D]" : "text-slate-400")}`}>
                    {formatted || (
                      <MetricDisplay value={null} format="raw" label={r.id} tooltipText={r.tooltip} size="small" />
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </SectionCard>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 4) CCC
// ---------------------------------------------------------------------------
function CCCBar({ label, days, max, status, hint }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.blue;
  const numericDays = (days === null || days === undefined || Number.isNaN(Number(days))) ? null : Number(days);
  const w = numericDays === null ? 0 : Math.max(8, Math.min(100, (numericDays / max) * 100));
  return (
    <div>
      <div className="flex items-baseline justify-between text-[12.5px]">
        <span className="font-medium text-[#0F1B2D]">{label}</span>
        <span className={`font-semibold ${numericDays === null ? "text-slate-400" : s.text}`}>
          {numericDays === null ? DASH : `${numericDays} days`}
        </span>
      </div>
      <div className="mt-1 h-2 w-full rounded-full bg-slate-100">
        <div className={`h-2 rounded-full ${s.bar}`} style={{ width: `${w}%`, transition: "width 600ms ease" }} />
      </div>
      {hint && <div className="mt-1 text-[11px] text-slate-500">{hint}</div>}
    </div>
  );
}

function CFOCcc() {
  const { customerData, dataAvailable, isLoading } = useCustomerData();

  // Visitors — hardcoded demo CCC view unchanged.
  

  // Active customer
  const eff       = (dataAvailable && customerData?.efficiency)       || {};
  const debt      = (dataAvailable && customerData?.debtorAnalytics)  || {};
  const inv       = (dataAvailable && customerData?.inventoryAnalytics) || {};
  const raw       = (dataAvailable && customerData?.raw)              || {};
  const debtors   = (dataAvailable && customerData?.debtors)          || [];

  const dso = eff.dso_days ?? null;
  const dio = eff.dio_days ?? null;
  const dpo = eff.dpo_days ?? null;
  const ccc = eff.ccc_days ?? null;
  const max = Math.max(Number(dso) || 0, Number(dio) || 0, Number(dpo) || 0, Number(ccc) || 0, 1);

  const statusFromDays = (v, hi, mid) => {
    if (v === null || v === undefined) return "blue";
    if (v >= hi)  return "red";
    if (v >= mid) return "amber";
    return "green";
  };

  const dsoHint = (debt.largest_debtor_name && debt.largest_debtor_amount)
    ? `Largest outstanding: ${debt.largest_debtor_name} — ${fmtINR(debt.largest_debtor_amount) || DASH}`
    : DASH;
  const dioHint = (raw.inventory_total_value ?? null) !== null
    ? `Inventory value held: ${fmtINR(raw.inventory_total_value) || DASH}`
    : ((inv.total_value ?? null) !== null
        ? `Inventory value held: ${fmtINR(inv.total_value) || DASH}`
        : DASH);
  const cccHint = (ccc !== null && raw.revenue_mtd)
    ? `At ${fmtINR(raw.revenue_mtd)} revenue, every extra day ties up ~${fmtINR(Number(raw.revenue_mtd) / 30) || DASH}`
    : DASH;
  const dpoHint = (raw.total_creditors ?? null) !== null && Number(raw.total_creditors) > 0
    ? `Outstanding to suppliers: ${fmtINR(raw.total_creditors) || DASH}`
    : DASH;

  // v1.5.1 (Feb 2026 — unsupported-claim cleanup): "vs 28-day benchmark" was a
  // hardcoded industry figure with no citation or real cohort behind it — removed,
  // not replaced with another assumed number. Shows only the real computed CCC.
  const cccSubtitle = ccc !== null
    ? `Your CCC is ${fmtDays(ccc)}`
    : `${DASH} · connect Tally to compute your CCC`;

  // Build the overdue table — sort by days_outstanding desc.
  const sortedDebtors = debtors
    .filter((d) => d && d.outstanding_amount > 0)
    .slice()
    .sort((a, b) => (Number(b.days_outstanding) || 0) - (Number(a.days_outstanding) || 0));

  const riskFromDays = (d) => (d > 45 ? "red" : d > 30 ? "amber" : "green");

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3 desktop:grid-cols-4">
        <RealMiniKPI testId="ccc-kpi-dso" icon={Clock} label="DSO"
          value={dso} format="days" status={statusFromDays(dso, 45, 30)}
          sub={dso !== null ? "customer collection" : null}
          tooltip="Days Sales Outstanding — average days customers take to pay you." />
        <RealMiniKPI testId="ccc-kpi-dio" icon={Clock} label="DIO"
          value={dio} format="days" status={statusFromDays(dio, 60, 45)}
          sub={dio !== null ? "inventory holding" : null}
          tooltip="Days Inventory Outstanding — days you hold stock before it converts to sales." />
        <RealMiniKPI testId="ccc-kpi-dpo" icon={Clock} label="DPO"
          value={dpo} format="days" status="amber"
          sub={dpo !== null ? "supplier payments" : null}
          tooltip="Days Payable Outstanding — average days you take to pay suppliers." />
        <RealMiniKPI testId="ccc-kpi-ccc" icon={Clock} label="CCC"
          value={ccc} format="days" status={statusFromDays(ccc, 60, 45)}
          sub={ccc !== null ? "DSO + DIO − DPO" : null}
          tooltip="Cash Conversion Cycle = DSO + DIO − DPO. Lower is better." />
      </div>

      <SectionCard
        testid="cfo-ccc-formula"
        title="How your Cash Conversion Cycle is built"
        subtitle={cccSubtitle}
      >
        <div className="space-y-4">
          <CCCBar label="DSO — days to collect from customers" days={dso} max={max} status={statusFromDays(dso, 45, 30)} hint={dsoHint} />
          <CCCBar label="DIO — days you hold inventory"        days={dio} max={max} status={statusFromDays(dio, 60, 45)} hint={dioHint} />
          <CCCBar label="DPO — days you take to pay suppliers" days={dpo} max={max} status="amber" hint={dpoHint} />
          <CCCBar label="CCC = DSO + DIO − DPO"                days={ccc} max={max} status={statusFromDays(ccc, 60, 45)} hint={cccHint} />
        </div>
      </SectionCard>

      <SectionCard
        testid="cfo-overdue-invoices"
        title="Top overdue receivables"
        subtitle={sortedDebtors.length > 0 ? `${sortedDebtors.length} outstanding · biggest cash levers right now` : "Biggest cash levers right now"}
      >
        {!dataAvailable ? (
          <div className="rounded-md border border-dashed border-slate-200 bg-slate-50 px-3 py-4 text-center text-[12px] text-slate-500">
            {DASH} · debtor data appears after your first Tally sync with open invoices.
          </div>
        ) : sortedDebtors.length === 0 ? (
          <div className="rounded-md border border-dashed border-emerald-200 bg-emerald-50/40 px-3 py-4 text-center text-[12px] text-emerald-700">
            No outstanding debtors.
          </div>
        ) : (
          <table className="w-full text-[12.5px]">
            <thead>
              <tr className="text-left text-[10.5px] uppercase tracking-wider text-slate-500">
                <th className="py-2">Customer</th>
                <th className="py-2 text-right">Amount</th>
                <th className="py-2 text-right">Days</th>
                <th className="py-2 text-right">Risk</th>
              </tr>
            </thead>
            <tbody>
              {sortedDebtors.map((r, i) => {
                const days = Number(r.days_outstanding) || 0;
                const risk = riskFromDays(days);
                return (
                  <tr key={`${r.customer_name}-${i}`} data-testid={`ccc-inv-real-${i}`} className="border-t border-slate-100">
                    <td className="py-2 font-medium text-[#0F1B2D]">{r.customer_name || DASH}</td>
                    <td className="py-2 text-right tabular-nums text-[#0F1B2D]">{fmtINR(r.outstanding_amount) || DASH}</td>
                    <td className="py-2 text-right tabular-nums text-[#0F1B2D]">{days}d</td>
                    <td className="py-2 text-right">
                      <StatusPill status={risk}>{risk === "red" ? "Overdue" : risk === "amber" ? "Watch" : "On track"}</StatusPill>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </SectionCard>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 5) INVENTORY
// ---------------------------------------------------------------------------
function CFOInventory() {
  const { customerData, dataAvailable, isLoading } = useCustomerData();

  // Visitor / demo session — keep hardcoded inventory view.
  

  // Active customer
  const inv     = (dataAvailable && customerData?.inventoryAnalytics) || {};
  const eff     = (dataAvailable && customerData?.efficiency)         || {};
  const items   = Array.isArray(inv.items_raw) ? inv.items_raw : (Array.isArray(customerData?.inventoryItems) ? customerData.inventoryItems : []);

  const totalValue    = inv.total_value      ?? null;
  const slowMoving    = inv.slow_moving_value ?? null;
  const turns         = eff.inventory_turns  ?? null;

  // Sort items by value desc.
  const sortedItems = items
    .filter((it) => it && (it.value || it.outstanding_amount || it.amount))
    .slice()
    .sort((a, b) => (Number(b.value || b.amount || 0)) - (Number(a.value || a.amount || 0)));

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3 desktop:grid-cols-4">
        <RealMiniKPI
          testId="inv-kpi-value"
          icon={Package} label="Inventory Value"
          value={totalValue} format="currency" status="amber"
          sub={totalValue !== null ? "from your Tally stock" : null}
          tooltip="Total inventory value (closing stock) from your latest Tally sync."
        />
        <RealMiniKPI
          testId="inv-kpi-slow"
          icon={Package} label="Slow Moving"
          value={slowMoving} format="currency" status="red"
          sub={slowMoving !== null && totalValue ? `${Math.round((slowMoving / totalValue) * 100)}% of stock` : null}
          tooltip="Inventory that hasn't moved in 90+ days — locked working capital."
        />
        <RealMiniKPI
          testId="inv-kpi-turns"
          icon={Package} label="Inventory Turns"
          value={turns} format="number" status="amber"
          sub={turns !== null ? "rolling 90d" : null}
          tooltip="Times your inventory is sold and replaced in a year — higher is healthier."
        />
        <RealMiniKPI
          testId="inv-kpi-reorder"
          icon={AlertTriangle} label="Reorder Alerts"
          value={null} format="number" status="amber"
          tooltip="Configure minimum stock levels to enable reorder alerts."
        />
      </div>

      <div className="grid grid-cols-1 gap-4 tablet:grid-cols-2 desktop:grid-cols-5">
        <div className="desktop:col-span-3">
          <SectionCard
            testid="cfo-inv-top"
            title="Top items by value"
            subtitle={sortedItems.length > 0 ? `${sortedItems.length} items synced from Tally` : "From your Tally stock ledger"}
          >
            {sortedItems.length === 0 ? (
              <div className="rounded-md border border-dashed border-slate-200 bg-slate-50 px-3 py-6 text-center text-[12px] text-slate-500">
                No inventory items found. Inventory data appears after Tally syncs.
              </div>
            ) : (
              <table className="w-full text-[12.5px]">
                <thead>
                  <tr className="text-left text-[10.5px] uppercase tracking-wider text-slate-500">
                    <th className="py-2">Item</th>
                    <th className="py-2 text-right">Qty</th>
                    <th className="py-2 text-right">Value</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedItems.slice(0, 10).map((r, i) => {
                    const name = r.name || r.item || r.stock_item || `Item ${i + 1}`;
                    const qty = r.quantity != null
                      ? `${r.quantity}${r.unit ? ` ${r.unit}` : ""}`
                      : (r.qty || null);
                    const value = r.value ?? r.amount ?? null;
                    return (
                      <tr key={`inv-${i}`} data-testid={`inv-row-real-${i}`} className="border-t border-slate-100">
                        <td className="py-2 font-medium text-[#0F1B2D]">{name}</td>
                        <td className="py-2 text-right tabular-nums text-slate-500">{qty || DASH}</td>
                        <td className="py-2 text-right tabular-nums text-[#0F1B2D]">{fmtINR(value) || DASH}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </SectionCard>
        </div>
        <div className="desktop:col-span-2">
          <SectionCard testid="cfo-inv-alerts" title="Reorder alerts" subtitle="Suppliers + lead times">
            <div className="flex items-start gap-2 rounded-md border border-dashed border-slate-200 bg-slate-50 px-3 py-4 text-[12px] text-slate-500">
              <span className="text-slate-400 text-[18px] leading-none">{DASH}</span>
              <span>Reorder thresholds can be configured once inventory data is syncing.</span>
            </div>
          </SectionCard>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 6) MARGINS
// ---------------------------------------------------------------------------
function CFOMargins() {
  const { customerData, dataAvailable, isLoading } = useCustomerData();

  // Visitor / demo session — keep hardcoded margins view.
  

  // Active customer
  const prof = (dataAvailable && customerData?.profitability) || {};
  const gm   = prof.gross_margin_pct ?? null;
  const em   = prof.ebitda_margin    ?? null;

  // Build the "last 8 months" strip — current month + 7 placeholder months.
  const now = new Date();
  const monthBars = Array.from({ length: 8 }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - (7 - i), 1);
    return { m: monthYearShort(d), v: i === 7 && gm !== null ? Number(gm) : null };
  });
  const realValues = monthBars.map((b) => b.v).filter((v) => v !== null);
  const tMin = realValues.length ? Math.min(...realValues) : 0;
  const tMax = realValues.length ? Math.max(...realValues) : 1;
  const tRange = Math.max(0.1, tMax - tMin);

  const trendSubtitle = gm !== null
    ? `This month: ${fmtPct(gm)}. Historical trend builds over time.`
    : `${DASH} · margin appears after Tally sync`;

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3 desktop:grid-cols-4">
        <RealMiniKPI
          testId="margin-kpi-gross"
          icon={TrendingUp} label="Gross Margin"
          value={gm} format="percentage"
          status={gm !== null && gm < 15 ? "red" : gm !== null && gm < 20 ? "amber" : "green"}
          sub={gm !== null ? "this period" : null}
          tooltip="Revenue minus direct costs as a percentage of revenue."
        />
        <RealMiniKPI
          testId="margin-kpi-ebitda"
          icon={TrendingUp} label="EBITDA Margin"
          value={em} format="percentage"
          status={em !== null && em < 5 ? "red" : em !== null && em < 8 ? "amber" : "green"}
          sub={em !== null ? "this period" : null}
          tooltip="EBITDA divided by revenue — operational profitability margin."
        />
        <RealMiniKPI
          testId="margin-kpi-leak"
          icon={AlertTriangle} label="Margin Leak Jobs"
          value={null} format="number" status="amber"
          tooltip="Job-level margins require production order data."
        />
        <RealMiniKPI
          testId="margin-kpi-best"
          icon={TrendingUp} label="Best Job"
          value={null} format="number" status="green"
          tooltip="Connects with MES or ERP system."
        />
      </div>

      <SectionCard
        testid="cfo-margin-trend"
        title="Gross margin trend"
        subtitle={trendSubtitle}
        right={gm !== null ? <span className="text-[11px] text-emerald-600">{fmtPct(gm)}</span> : <span className="text-[11px] text-slate-400">{DASH}</span>}
      >
        {gm === null ? (
          <div className="flex h-[110px] items-center justify-center rounded-md border border-dashed border-slate-200 bg-slate-50 text-[12px] text-slate-500">
            <span className="text-slate-400 mr-2 text-[18px] leading-none">{DASH}</span>
            Historical data accumulates over time.
          </div>
        ) : (
          <div data-testid="margin-trend-real">
            <div className="flex h-[110px] gap-2">
              {monthBars.map((d, i) => {
                const isCurrent = d.v !== null;
                const h = isCurrent ? 25 + ((d.v - tMin) / tRange) * 75 : 0;
                return (
                  <div key={d.m + i} className="flex h-full flex-1 flex-col">
                    <div className="flex flex-1 items-end">
                      {isCurrent ? (
                        <div className="w-full rounded-t-sm bg-emerald-500 transition-all hover:opacity-90" style={{ height: `${h}%` }} title={`${fmtPct(d.v)}`} />
                      ) : (
                        <div className="w-full rounded-t-sm bg-slate-100" style={{ height: "20%" }} title="Historical data accumulates over time" />
                      )}
                    </div>
                    <div className="mt-1 text-center text-[10px] text-slate-500">{d.m}</div>
                  </div>
                );
              })}
            </div>
            <div className="mt-2 text-[11px] text-slate-400">Historical data accumulates over time.</div>
          </div>
        )}
      </SectionCard>

      <SectionCard
        testid="cfo-job-margins"
        title="Per-job margin variance"
        subtitle="Job-level analysis appears when production data is connected"
      >
        <div className="flex items-start gap-2 rounded-md border border-dashed border-slate-200 bg-slate-50 px-3 py-6 text-[12px] text-slate-500">
          <span className="text-slate-400 text-[18px] leading-none">{DASH}</span>
          <span>Job-level margin analysis will appear here once production order data is connected.</span>
        </div>
      </SectionCard>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Module shell
// ---------------------------------------------------------------------------
export default function VirtualCFO() {
  const location = useLocation();
  const navigate = useNavigate();
  const [active, setActive] = useState("overview");
  const { customerData, dataAvailable, isLoading } = useCustomerData();

  useEffect(() => {
    const next = TAB_HASHES.includes(location.hash) ? location.hash.slice(1) : "overview";
    setActive(next);
  }, [location.hash]);

  const goTab = (id) => {
    setActive(id);
    navigate({ pathname: "/cfo", hash: `#${id}` }, { replace: false });
  };

  const lastSyncIso = customerData?.meta?.last_sync_at || customerData?.last_sync || null;
  const lastSyncLabel = true
    ? (lastSyncIso ? `Tally · last sync ${relativeTimeFrom(lastSyncIso) || DASH}` : `${DASH} · awaiting Tally sync`)
    : "Powered by Tally + Bank · last sync 3m ago";

  return (
    <div data-testid="cfo-module">
      <ConnectTallyBanner dataAvailable={dataAvailable} isLoading={isLoading} />
      <div className="px-4 tablet:px-5 desktop:px-6 py-4 desktop:py-5">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-2">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            CFO
          </div>
          <h2 className="text-[20px] font-bold text-[#1E3A5F]">
            Your finance brain · {monthYearLabel()}
          </h2>
        </div>
        <div data-testid="cfo-last-sync" className="hidden md:block text-[12px] text-slate-500">
          {lastSyncLabel}
        </div>
      </div>

      <div data-testid="cfo-tabs" className="mb-5 flex flex-wrap gap-1 border-b border-slate-200">
        {TABS.map((t) => {
          const isActive = active === t.id;
          return (
            <button
              key={t.id}
              data-testid={`cfo-tab-${t.id}`}
              onClick={() => goTab(t.id)}
              className={[
                "relative -mb-px px-3 py-2 text-[12.5px] font-medium transition-colors",
                isActive
                  ? "border-b-2 border-[#2563EB] text-[#1E3A5F]"
                  : "border-b-2 border-transparent text-slate-500 hover:text-[#1E3A5F]",
              ].join(" ")}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {active === "overview"  && <CFOOverview />}
      {active === "pnl"       && <CFOPnl />}
      {active === "cashflow"  && <CFOCashflow />}
      {active === "ccc"       && <CFOCcc />}
      {active === "inventory" && <CFOInventory />}
      {active === "margins"   && <CFOMargins />}
      </div>
    </div>
  );
}
